/**
 * Auth context object, value type, and the `useAuth` hook.
 *
 * Split out of AuthContext.tsx so that the provider file exports ONLY a
 * component — required by react-refresh/only-export-components (a mixed
 * component + hook/const export breaks Fast Refresh). Import the provider from
 * ./AuthContext and the hook from here.
 */
import { createContext, useContext } from "react";
import type {
  AuthenticatedUser, AuthChallenge,
  ForgotPasswordResult, ConfirmForgotPasswordResult,
} from "../lib/auth";
import type { CognitoUser } from "amazon-cognito-identity-js";

export interface AuthContextValue {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  signIn: (username: string, password: string) => Promise<AuthChallenge>;
  completeNewPassword: (
    cognitoUser: CognitoUser, newPassword: string,
  ) => Promise<AuthChallenge>;
  forgotPassword: (username: string) => Promise<ForgotPasswordResult>;
  confirmForgotPassword: (
    username: string, code: string, newPassword: string,
  ) => Promise<ConfirmForgotPasswordResult>;
  signOut: () => void;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside <AuthProvider>");
  return ctx;
}
